import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BackButton from '@/components/feature/BackButton';

type Step = 'identification' | 'verification' | 'success';
type ContactType = 'phone' | 'email';

export default function CreerComptePatientPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('identification');
  const [activationCode, setActivationCode] = useState('');
  const [numeroDossier, setNumeroDossier] = useState('');
  const [contactType, setContactType] = useState<ContactType>('phone');
  const [contactValue, setContactValue] = useState('');

  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setError('');
    if (!activationCode.trim() || !numeroDossier.trim() || !contactValue.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/start-patient-account-activation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activationCode, numeroDossier, contactType, contactValue }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur lors de la vérification');
      setStep('verification');
      startResendTimer();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    setError('');
    if (verificationCode.length !== 6) {
      setError('Veuillez saisir le code à 6 chiffres');
      return;
    }
    if (password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/complete-patient-account-activation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activationCode, numeroDossier, contactType, contactValue, verificationCode, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur lors de l\'activation');
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="mb-6">
            <BackButton />
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
            {/* Étape succès */}
            {step === 'success' ? (
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-clinic-green/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-5xl text-clinic-green"></i>
                </div>
                <h2 className="text-3xl font-bold text-clinic-blue-dark mb-3">Compte activé !</h2>
                <p className="text-gray-600 mb-6">
                  Votre compte patient est maintenant associé à votre dossier médical. Vous pouvez désormais accéder à votre historique de consultations et de rendez-vous.
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Me connecter
                </Link>
              </div>
            ) : step === 'identification' ? (
              /* Étape 1 : identification */
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-key-2-line text-3xl text-clinic-blue"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-clinic-blue-dark">Créer mon compte patient</h2>
                  <p className="text-gray-600 mt-2">
                    Entrez le code d'activation remis à la clinique
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label htmlFor="activation-code" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Code d'activation <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="activation-code"
                      type="text"
                      value={activationCode}
                      onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm font-mono tracking-wider text-center"
                      placeholder="K7F9-2M8Q-X4"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="numero-dossier" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Numéro de dossier <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="numero-dossier"
                      type="text"
                      value={numeroDossier}
                      onChange={(e) => setNumeroDossier(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm tracking-wider text-center"
                      placeholder="Ex: CSF1393"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-clinic-blue-dark mb-3">
                      Vérifier avec
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setContactType('phone')}
                        className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                          contactType === 'phone'
                            ? 'bg-clinic-blue text-white border-clinic-blue'
                            : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                        }`}
                      >
                        <i className="ri-whatsapp-line mr-2"></i>
                        Téléphone
                      </button>
                      <button
                        type="button"
                        onClick={() => setContactType('email')}
                        className={`flex-1 px-4 py-3 rounded-2xl border-2 transition-all whitespace-nowrap cursor-pointer ${
                          contactType === 'email'
                            ? 'bg-clinic-blue text-white border-clinic-blue'
                            : 'bg-white text-gray-700 border-clinic-blue/20 hover:border-clinic-blue/40'
                        }`}
                      >
                        <i className="ri-mail-line mr-2"></i>
                        Email
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-value" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      {contactType === 'phone' ? 'Numéro de téléphone' : 'Adresse email'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="contact-value"
                      type={contactType === 'phone' ? 'tel' : 'email'}
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder={contactType === 'phone' ? '+229 XX XX XX XX' : 'votre@email.com'}
                      autoComplete="off"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      Ce contact doit correspondre à celui inscrit dans votre dossier.
                    </p>
                  </div>

                  <button
                    onClick={handleSendCode}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {loading ? 'Vérification...' : 'Continuer'}
                  </button>
                </div>
              </>
            ) : (
              /* Étape 2 : vérification + mot de passe */
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-3xl text-clinic-blue"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-clinic-blue-dark">Vérification</h2>
                  <p className="text-gray-600 mt-2">
                    Un code à 6 chiffres a été envoyé via {contactType === 'phone' ? 'WhatsApp ou SMS' : 'email'}
                  </p>
                  <p className="text-clinic-blue font-semibold mt-1">{contactValue}</p>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label htmlFor="verif-code" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Code de vérification
                    </label>
                    <input
                      id="verif-code"
                      type="text"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-center text-2xl font-mono tracking-widest"
                      placeholder="000000"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                        placeholder="Minimum 10 caractères"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-clinic-blue-dark transition-colors cursor-pointer"
                        aria-label={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-clinic-blue-dark mb-2">
                      Confirmer le mot de passe
                    </label>
                    <input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                      placeholder="Répétez le mot de passe"
                    />
                  </div>

                  <button
                    onClick={handleFinalize}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {loading ? 'Création du compte...' : 'Créer mon compte'}
                  </button>

                  <div className="text-center">
                    {resendTimer > 0 ? (
                      <p className="text-gray-600 text-sm">Renvoyer le code dans {resendTimer}s</p>
                    ) : (
                      <button
                        onClick={handleSendCode}
                        disabled={loading}
                        className="text-clinic-blue hover:text-clinic-blue-dark font-semibold text-sm cursor-pointer"
                      >
                        Renvoyer le code
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => setStep('identification')}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>
                    Modifier mes informations
                  </button>
                </div>
              </>
            )}

            {step !== 'success' && (
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-clinic-blue hover:text-clinic-blue-dark font-semibold transition-colors whitespace-nowrap">
                    Connectez-vous
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}